import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { Eye, Pencil, Trash2, Pause, Play, Plus, Share2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function StatusPill({ status, t }) {
  const cls = {
    published: "fl-status fl-status-published",
    pending: "fl-status fl-status-pending",
    rejected: "fl-status fl-status-rejected",
    paused: "fl-status fl-status-paused",
    draft: "fl-status fl-status-draft",
  }[status] || "fl-status fl-status-paused";
  return <span className={cls}>{t.status[status] || status}</span>;
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const load = () => api.get("/my/properties").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const stats = {
    total: items.length,
    published: items.filter((x) => x.status === "published").length,
    pending: items.filter((x) => x.status === "pending").length,
    paused: items.filter((x) => x.status === "paused").length,
    views: items.reduce((s, x) => s + (x.views || 0), 0),
  };
  const toggle = async (p) => {
    const next = p.status === "published" ? "paused" : "published";
    await api.patch(`/properties/${p.id}/status`, { status: next });
    toast.success(next === "paused" ? (lang === "ar" ? "تم إيقاف الإعلان" : "Paused") : (lang === "ar" ? "أعيد إلى المراجعة" : "Sent to review"));
    load();
  };
  const del = async (p) => {
    await api.delete(`/properties/${p.id}`);
    toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
    load();
  };
  const share = async (p) => {
    const url = `${window.location.origin}/property/${p.id}`;
    try { await navigator.share({ title: p.title, url }); }
    catch { await navigator.clipboard.writeText(url); toast.success(t.detail.copyLink); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-emerald-950">{t.dash.title}</h1>
        <Button data-testid="dash-add" onClick={() => nav("/sell")} className="bg-amber-500 hover:bg-amber-600 text-emerald-950 gap-1"><Plus className="w-4 h-4" /> {t.nav.add}</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          ["total", stats.total], ["published", stats.published],
          ["pending", stats.pending], ["paused", stats.paused], ["views", stats.views],
        ].map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-xs text-slate-500">{t.dash.stats[k]}</div>
            <div className="fl-num text-2xl font-extrabold text-emerald-950 mt-1">{formatPrice(v)}</div>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-2xl border border-slate-200">{t.dash.empty}</div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => {
            const cover = p.images?.[p.cover_index || 0];
            return (
              <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-col sm:flex-row gap-3 items-stretch">
                <div className="sm:w-40 aspect-video sm:aspect-square rounded-lg overflow-hidden bg-slate-100 shrink-0">
                  {cover && <img src={fileUrl(cover)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="fl-status fl-status-paused">{typeLabel(p.type, lang)}</span>
                    <StatusPill status={p.status} t={t} />
                  </div>
                  <div className="font-bold text-emerald-950">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.governorate}{p.city ? ` · ${p.city}` : ""}{p.area ? ` · ${p.area}` : ""}</div>
                  <div className="fl-num font-extrabold text-emerald-950">{formatPrice(p.price)} {t.common.currency}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {p.views}</div>
                  {p.status === "pending" && (
                    <div className="mt-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1 w-fit">
                      <AlertCircle className="w-3.5 h-3.5" /> {t.dash.underReview}
                    </div>
                  )}
                  {p.status === "rejected" && p.rejection_reason && (
                    <div className="mt-1 text-xs text-rose-900 bg-rose-50 border border-rose-200 rounded-md px-2 py-1 flex items-start gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span><b>{t.dash.rejReason}:</b> {p.rejection_reason}</span>
                    </div>
                  )}
                </div>
                <div className="flex sm:flex-col gap-2 items-stretch flex-wrap">
                  {p.status === "published" && (
                    <Button size="sm" variant="outline" onClick={() => nav(`/property/${p.id}`)} className="gap-1"><Eye className="w-4 h-4" /> {t.dash.view}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => nav(`/sell/${p.id}`)} className="gap-1"><Pencil className="w-4 h-4" /> {t.dash.edit}</Button>
                  {p.status !== "rejected" && p.status !== "pending" && (
                    <Button size="sm" variant="outline" onClick={() => toggle(p)} className="gap-1">
                      {p.status === "published" ? <><Pause className="w-4 h-4" /> {t.dash.pause}</> : <><Play className="w-4 h-4" /> {t.dash.resume}</>}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => share(p)} className="gap-1"><Share2 className="w-4 h-4" /> {t.dash.share}</Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="gap-1 text-rose-600 hover:text-rose-700" data-testid={`dash-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" /> {t.dash.delete}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t.dash.confirmDelTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{t.dash.confirmDelDesc}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t.dash.cancel}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(p)} className="bg-rose-600 hover:bg-rose-700" data-testid={`dash-delete-confirm-${p.id}`}>{t.dash.confirmDel}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
